import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { filterSuccessResult, injectQuery, injectQueryClient } from '@ngneat/query';
import { IBaseResponse, INews } from '@senet-id/core/types';
import { StorageService } from '@senet-id/core/services';
import { updateListEntity } from '@senet-uiverse/common';
import { map, tap } from 'rxjs';

interface IGetNewsListParams {
  offices: number[];
  page: number;
  size: number;
}

@Injectable()
export class NewsService {
  private readonly storageService = inject(StorageService);
  private readonly queryClient = injectQueryClient();
  private readonly http = inject(HttpClient);
  private readonly query = injectQuery();

  getNewsList(params: Readonly<IGetNewsListParams>) {
    const organization = this.storageService.storage().organization;
    const url = `articles/${organization}/`;

    return this.http.get<IBaseResponse<INews> | undefined>(url, {
      params: { ...params, ordering: '-mod_date' },
    });
  }

  getNewsById(id: Readonly<number>) {
    const organization = this.storageService.storage().organization;
    const url = `articles/${organization}/${id}/`;

    return this.query({
      queryKey: [url],
      queryFn: () => this.http.get<INews>(url),
    }).result$.pipe(
      filterSuccessResult(),
      map(({ data }) => data),
    );
  }

  markNewsAsRead(id: Readonly<number>) {
    const organization = this.storageService.storage().organization;

    return this.http.post<INews>(`/articles/${organization}/${id}/read/`, {});
  }

  toggleNewsLike(article: Readonly<INews>) {
    return article.is_liked ? this.dislike(article.id) : this.like(article.id);
  }

  private like(id: Readonly<number>) {
    const organization = this.storageService.storage().organization;

    return this.http
      .post<INews>(`/articles/${organization}/${id}/like/`, {})
      .pipe(tap((response) => this.updateStoredNews(id, response)));
  }

  private dislike(id: Readonly<number>) {
    const organization = this.storageService.storage().organization;

    return this.http
      .post<INews>(`/articles/${organization}/${id}/dislike/`, {})
      .pipe(tap((response) => this.updateStoredNews(id, response)));
  }

  private updateStoredNews(id: number, update: Partial<INews>) {
    const organization = this.storageService.storage().organization;
    const url = `articles/${organization}/`;

    this.queryClient.setQueryData([url], updateListEntity<INews>(id, update));
  }
}
